/**
 * Thanksgiving embroidery patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingEmbroideryPatternsPack: ToilePack = {
    slug: 'thanksgiving-embroidery-patterns',
    category: 'season',
    name: 'Thanksgiving embroidery patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'wreath', label: 'Leaf wreath', subject: 'A round wreath of autumn leaves and acorns'},
        {key: 'turkey', label: 'Turkey', subject: 'A turkey with a fanned tail'},
        {key: 'pumpkins', label: 'Pumpkin row', subject: 'A row of three pumpkins with leaves'},
    ],
    styles: [
        {key: 'simple', label: 'Simple outline', style: 'a bold single-line running-stitch transfer outline, wide uniform stroke', palette: 'Black ink only', framing: 'Centered on a pure white page with a wide margin'},
        {key: 'detailed', label: 'Detailed pattern', style: 'a detailed pattern with fine double-line transfer guides and dense satin-stitch fill hatching', palette: 'Black ink only', framing: 'Filling the page within a moderate white margin'},
        {key: 'redwork', label: 'Redwork style', style: 'a thin uniform redwork-style outline, delicate folk-art linework', palette: 'Black ink only', framing: 'Centered on a pure white page with a wide margin'},
    ],
    directions: ['a monogram instead', 'for a table runner', 'as a mini hoop design'],
    directionHint: 'try: a monogram instead',
    seo: {
        title: 'Thanksgiving embroidery: wreath, turkey, pumpkins',
        metaDescription: 'Nine Thanksgiving embroidery transfer patterns — a leaf wreath, a turkey, a pumpkin row — as simple outline, detailed or redwork stitch guides.',
        h1: 'Thanksgiving embroidery patterns: wreath, turkey, pumpkins',
        tagline: 'Nine transfer patterns, three autumn motifs, three stitch-guide styles each — ready to trace onto fabric.',
        intro: 'An embroidery transfer pattern for the hoop: a round wreath of autumn leaves, a turkey with a fanned tail, a row of three pumpkins. Each is drawn as a bold single-line running-stitch outline, a detailed pattern with satin-stitch fill guides, or a delicate redwork-style line. Fabric-free black-on-white line art, ready to trace. Ask for a different motif, like a monogram, and it redraws in the same three styles.',
        useCases: [
            'a transfer pattern for the embroidery hoop',
            'a Thanksgiving table-linen design',
            'a small gift for a fellow stitcher',
            "a beginner's first autumn hoop project",
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each pattern is centered on a square page with a wide white margin, sized to trace onto standard hoop fabric.'},
            {q: 'Do I need special transfer paper?', a: 'Any iron-on transfer pencil or light-box tracing method works — the pattern is clean black line art on white, ready either way.'},
            {q: "What's the difference between the three stitch-guide styles?", a: 'Simple outline is a bold single running-stitch line, detailed pattern adds satin-stitch fill guides, and redwork is a thin delicate folk-art line — three ways stitchers actually work a hoop design.'},
            {q: 'Can I get a different autumn motif?', a: "Yes — that's what the direction field is for. Type a different motif, like a monogram or a table runner, and it redraws in the same three styles."},
        ],
    },
}
